import { useEffect, useRef, useState } from 'react';
import { IconButton } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { CustomFieldDefinition } from '../../../types';
import { Text } from '../../Atoms/Text';
import { CustomFieldValueEditor } from './CustomFieldValueEditor';
import { customFieldDisplayValue, editableCustomFieldValue } from '../../../utils/customFieldTypes';

export interface CustomFieldDetailRowProps {
  definition: CustomFieldDefinition;
  value: string;
  onSave: (definitionId: string, value: string) => Promise<void>;
  gutterBottom?: boolean;
  readOnly?: boolean;
}

export function CustomFieldDetailRow({
  definition,
  value,
  onSave,
  gutterBottom = true,
  readOnly = false
}: CustomFieldDetailRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [saving, setSaving] = useState(false);
  const [editValid, setEditValid] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  // Dropping the draft rather than holding it: a field locked mid-edit would otherwise re-open on the stale text.
  useEffect(() => {
    if (readOnly) setIsEditing(false);
  }, [readOnly]);

  const handleEdit = () => {
    setEditValue(editableCustomFieldValue(definition.field_type, value));
    setEditValid(true);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editValid) return;
    setSaving(true);
    try {
      await onSave(definition.id, editValue);
      setIsEditing(false);
    } catch {
      // error handled by caller via snackbar; stay in edit mode
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    setEditValid(true);
    setIsEditing(false);
  };

  return (
    <div className={`flex items-center${gutterBottom ? ' mb-8' : ''}`}>
      <Text
        className="font-sans text-grey-4 w-full"
        size="sm14">
        {definition.name}
      </Text>
      {isEditing && !readOnly ? (
        <div className="flex items-center gap-1 w-full">
          <CustomFieldValueEditor
            definition={definition}
            value={editValue}
            onChange={setEditValue}
            disabled={saving}
            inputRef={inputRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
            onValidityChange={setEditValid}
            storedValue={value}
          />
          <IconButton
            size="small"
            onClick={handleSave}
            aria-label={`Save ${definition.name}`}
            disabled={saving || !editValid}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleCancel}
            aria-label={`Cancel ${definition.name}`}
            disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      ) : (
        <div className="flex items-center w-full">
          <Text
            className="font-sans font-normal flex-1"
            size="sm">
            {customFieldDisplayValue(definition.field_type, value) || '—'}
          </Text>
          {!readOnly && (
            <IconButton
              size="small"
              onClick={handleEdit}
              aria-label={`Edit ${definition.name}`}
              sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      )}
    </div>
  );
}
